import { IMutateAllocationBagValue } from "actions/collections";
import { OperationType } from "actions/mutate";
import { ALLOCATION_DIMENSION_ID_PREFIX } from "constants/product-collection";
import { ISchemaKey } from "helpers/product-collection";
import { fromJS, Map } from "immutable";
import { safeParseFloat } from "lib/number";
import lodashGet from "lodash/get";
import lodashMemoize from "lodash/memoize";
import memoizeOne from "memoize-one";

export enum AllocationDimensionOwnerObject {
    Merchant = "Merchant",
    Collection = "Collection",
}

export enum AllocationDimensionKind {
    Generic = "GENERIC",
    Size = "SIZE",
}

// Enum value is index of allocation dimension.
// The way we are wiring allocation dimension make order of allocation dimension matters,
// where dates is first and doors second.
// TODO: we should remove concept of door and dates from FE
export enum AllocationDimensionIndex {
    Date = 0,
    Door = 1,
}

export const AllocationDimensionFields = {
    attributes: ["attributes"],
    dimensionValues: ["dimension_values"],
};

export interface IAllocationDimension {
    id: string;
    revision: number;
    subtype: AllocationDimensionKind;
    active: boolean;
    name: string;
    dimension_values: IAllocationDimensionValue[];
    attributes: {
        keys: ISchemaKey[];
    };
}

export interface AllocationDimensionOptions {
    getOnlyUniqDates?: boolean;
    shipmentsWithUnits?: boolean;
}

export interface AllocationDimension {
    active: boolean;
    included?: boolean;
    id: string;
    name: string;
    group?: string;
}
export enum AllocationDimensionValueFields {
    ID = "id",
    ExternalId = "external_id",
    Name = "name",
    Active = "active",
    Included = "included",
    RetailPriceField = "allocation_retail_price",
    CostPriceField = "allocation_cost_price",
    Notes = "_door_notes",
}

export interface IAllocationDimensionValue extends AllocationDimension {
    external_id: string;

    fields?: {
        [field: string]: {
            string_value: string;
        };
    };
}
export interface ICreateAllocationDimensionValue extends Omit<IAllocationDimensionValue, "id"> {}
export interface IUpdateAllocationDimensionValue extends Partial<IAllocationDimensionValue> {}

export enum AllocationDimensionOperationKey {
    SetName = "set_name",
    SetActive = "set_active",
    SetAttributes = "set_attributes",
    InsertDimensionValue = "insert_dimension_value",
    SetDimensionValue = "set_dimension_value",
    MoveDimensionValue = "move_dimension_value",
}
export interface IAllocationDimensionOperation {
    [AllocationDimensionOperationKey.SetName]: string;
    [AllocationDimensionOperationKey.SetActive]: boolean;
    [AllocationDimensionOperationKey.SetAttributes]: {
        keys: ISchemaKey[];
    };
    [AllocationDimensionOperationKey.InsertDimensionValue]: {
        index: number;
        value: ICreateAllocationDimensionValue;
    };
    [AllocationDimensionOperationKey.SetDimensionValue]: {
        index: number;
        value: IUpdateAllocationDimensionValue;
    };
    [AllocationDimensionOperationKey.MoveDimensionValue]: {
        index: number;
        destination_index: number;
    };
}

export const buildMutateOperation = (
    operation: AllocationDimensionOperationKey,
    value: IAllocationDimensionOperation[AllocationDimensionOperationKey],
): Partial<IAllocationDimensionOperation> => {
    let operationValue;
    switch (operation) {
        case AllocationDimensionOperationKey.InsertDimensionValue:
        case AllocationDimensionOperationKey.SetDimensionValue:
        case AllocationDimensionOperationKey.MoveDimensionValue:
            operationValue = {
                ...(value as any),
            };
            break;
        default: {
            operationValue = {
                value,
            };
            break;
        }
    }
    // Not intuitive way to reorder. If destination_index > index, destination_index must increment 1
    if (operation === AllocationDimensionOperationKey.MoveDimensionValue) {
        if (operationValue.destination_index > operationValue.index) {
            operationValue.destination_index += 1;
        }
    }

    return {
        [operation]: operationValue,
    };
};

// Normalize allocation dimension on collection level, which is nested inside mutable mixed object.
export const normalizeCollectionAllocationDimension = (ref: any, data: any): IAllocationDimension => ({
    id: ref.object_id,
    revision: ref.revision,
    name: lodashGet(data, ["field_map", "map", "fields", "name", "string_value"]),
    subtype: lodashGet(data, ["field_map", "map", "fields", "subtype", "string_value"]),
    active: lodashGet(data, ["field_map", "map", "fields", "active", "bool_value"]),
    dimension_values: lodashGet(data, ["dimension_values", "list", "values"], []).map(
        (rawDimensionValue) => rawDimensionValue.struct_value,
    ),
    attributes: lodashGet(data, ["field_map", "map", "fields", "attributes", "struct_value"]),
});

// Normalize allocation dimension on collection level, which is nested inside mutable mixed object.
export const normalizeCollectionAllocationDimensionJS = (ref: any, data: any): IAllocationDimension => ({
    id: ref.object_id,
    revision: ref.revision,
    name: data.field_map?.map?.fields?.name?.string_value,
    subtype: data.field_map?.map?.fields?.subtype?.string_value,
    active: data.field_map?.map?.fields?.active?.bool_value,
    dimension_values: (data.dimension_values?.list?.values ?? []).map((raw) => raw.struct_value),
    attributes: data.field_map?.map?.fields?.attributes?.struct_value,
});

export const getGetterBreadcrumbs = memoizeOne(
    (genericAllocationDimensions: IAllocationDimension[], depth?: number) => {
        let allocationDimensionValueBreadcrumbs = [];
        let copiedAllocationDimensionValueBreadcrumbs = [];

        // Reverse-loop through allocation dimensions
        let allocationDimensionLoopCounter = depth || genericAllocationDimensions.length;

        // Negative means ignore n index from the end
        if (depth < 0) {
            allocationDimensionLoopCounter = genericAllocationDimensions.length - depth * -1;
        }

        const seenKey = {};
        let isFirstLoop = true;
        while (allocationDimensionLoopCounter > 0) {
            copiedAllocationDimensionValueBreadcrumbs = allocationDimensionValueBreadcrumbs;
            allocationDimensionValueBreadcrumbs = [];
            for (const allocationDimensionValue of genericAllocationDimensions[allocationDimensionLoopCounter - 1]
                .dimension_values) {
                const key = `${genericAllocationDimensions[allocationDimensionLoopCounter - 1].id}_${
                    allocationDimensionValue.id
                }`;
                if (!allocationDimensionValue.included || !allocationDimensionValue.active || seenKey[key]) {
                    continue;
                }

                seenKey[key] = true;

                // If there are previous breadcrumbs, prepend to each
                if (!isFirstLoop) {
                    for (const allocationDimensionValueBreadcrumb of copiedAllocationDimensionValueBreadcrumbs) {
                        allocationDimensionValueBreadcrumbs.push([
                            allocationDimensionValue.id,
                            ...allocationDimensionValueBreadcrumb,
                        ]);
                    }
                } else {
                    allocationDimensionValueBreadcrumbs.push([allocationDimensionValue.id]);
                }
            }
            isFirstLoop = false;
            allocationDimensionLoopCounter -= 1;
        }

        return allocationDimensionValueBreadcrumbs;
    },
);

export const matchBreadcrumbs = (breadcrumbs1: string[], breadcrumbs2: string[]) => {
    // Breadcrumbs1 is sparse array. Take undefined as wildcard.
    // For-of doesn't work with sparse array
    for (let i = 0; i < breadcrumbs1.length; i += 1) {
        if (breadcrumbs1[i] && breadcrumbs1[i] !== breadcrumbs2[i]) {
            return false;
        }
    }

    return true;
};

// HACK. This is hardcoding there will always be two allocation dimensions.
export const getAllocationDimensionByIndex = (allocationDimensions, allocationDimensionIndex) => {
    if (allocationDimensions.length >= 2) {
        return allocationDimensions[allocationDimensionIndex];
    }

    return allocationDimensions[allocationDimensionIndex - 1] || {};
};

export const ROLLUP_ALLOCATION_DIMENSION_DELIMITER = "==";

export const getUserDefinedClassificationName = (classification: string, collectionId: string) =>
    `${classification}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${collectionId}`;

export const isAllocationDimensionId = (id: string) => new RegExp(ALLOCATION_DIMENSION_ID_PREFIX, "i").test(id);
export enum AllocationBagType {
    allocations = 1,
    bulks = 2,
    pack_allocations = 3,
    inventory_units = 4,
    po_creation_exports = 5,
    target = 6,
}

/*
Mutable mixed bag key & path nomenclature
const bagValue = {                                AllocationBag
    '74d0cc6e-51da-4844-823f-03cfb36145dd#2': {   AllocationBagKey = itemId#type
        map: {
            fields: {
                '20#1': {                         BreadcrumbKey is the key, BagValues is the content
                    string_value: '1'
                },
                '27#1': {string_value: '2'},
                '27#2': {string_value: '3'},
            }
        }
    },
    aa434968-c928-4908-9d99-75759809f44c#2: {map: {…}},
}
[allocationBagKey, 'map', 'fields', breadcrumbKey] = CompoundPath
[allocationBagKey, 'map', 'fields', breadcrumbKey, 'string_value'] = ValuePath
*/

type AllocationBagKey = string;
type BreadcrumbKey = string;
export type BagValues = Map<BreadcrumbKey, Map<"string_value", string>>;
export type AllocationBag = Map<AllocationBagKey, Map<"map", Map<"fields", BagValues>>>;
export const BAG_VALUE_PARAMS_SEPARATOR = "#";

interface MutateBagValueCallback {
    (type: AllocationBagType, itemId: string, breadcrumb: string[], value: number): void;
}

/**
 * Generate path to access content of row's bag, which contains list of available quantities per dimensions
 * @param type Type of bag
 * @param itemId Id of item, eg: assortment row's id
 * @returns
 */
export const getAllocationBagPath = lodashMemoize(
    (type: AllocationBagType, itemId: string) => {
        return [[itemId, type].join(BAG_VALUE_PARAMS_SEPARATOR), "map", "fields"];
    },
    (type, itemId) => `${type}_${itemId}`,
);

/**
 * Generate direct path to get quantity
 * @param type Bag type
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @param includeValueKey Flag whether to return path of primitive value or wrapped value. Eg: "1" vs { "string_value": "1" }
 * @returns
 */
export const getAllocationBagValuePath = (
    type: AllocationBagType,
    itemId: string,
    breadcrumb: string | string[],
    includeValueKey: boolean = true,
): string[] => {
    const allocationBagPath = getAllocationBagPath(type, itemId);
    const breadcrumbKey = Array.isArray(breadcrumb) ? breadcrumb.join(BAG_VALUE_PARAMS_SEPARATOR) : breadcrumb;
    const path = allocationBagPath.concat([breadcrumbKey]);

    if (includeValueKey) {
        path.push("string_value");
    }

    return path;
};

/**
 * Get row's bag, which contains list of available quantities per dimensions. Eg: quantity of delivery "January" for door "NYC".
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @returns
 */
export const getBagValuesByRowId = (type: AllocationBagType, bagValue: AllocationBag, itemId: string): BagValues => {
    return bagValue?.getIn(getAllocationBagPath(type, itemId), Map()) ?? Map();
};

/**
 * Simple, get a quantity by bag type, row id and dimensions. Eg: give me quantity of row "A" for delivery "January" for door "NYC".
 * @param type Type of bag
 * @param allocationBag Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @returns
 */
export const getAllocationBagValue = (
    type: AllocationBagType,
    allocationBag: AllocationBag,
    itemId: string,
    breadcrumb: string | string[],
): number | undefined => {
    const stringValue = allocationBag?.getIn(getAllocationBagValuePath(type, itemId, breadcrumb));

    // if value is not found or 0, return undefined so that cell is empty
    return safeParseFloat(stringValue, 0) || undefined;
};

/**
 * Set a quantity to dimensions. Eg: set quantity for row "A" for delivery "January" for door "NYC".
 * @param type Type of bag
 * @param bagValue Bag itself
 * @param itemId Id of item, eg: assortment row's id
 * @param breadcrumb Dimension ids of quantity to retrive
 * @param value Value of to set
 * @param callback Callback to be used for easier mutation to server
 * @returns
 */
export const setBagValue = (
    type: AllocationBagType,
    bagValue: AllocationBag,
    itemId: string,
    breadcrumb: string[],
    value: number,
    callback?: MutateBagValueCallback,
): AllocationBag => {
    if (!+value) {
        callback?.(type, itemId, breadcrumb, 0);
        const path = getAllocationBagValuePath(type, itemId, breadcrumb);

        if (bagValue.hasIn(path)) {
            return bagValue.deleteIn(path);
        } else {
            return bagValue;
        }
    }

    callback?.(type, itemId, breadcrumb, +value);

    const path = getAllocationBagValuePath(type, itemId, breadcrumb, false);
    return bagValue.setIn(path, fromJS({ string_value: value.toString() }));
};

/**
 * This function will:
 * 1. Get the allocation bag using path [itemId#type, map, fields]
 * 2. Run callback on each element of the allocation bag
 * @param bagValue The whole bag, includes multiple allocation bags
    const bag = {
        '74d0cc6e-51da-4844-823f-03cfb36145dd#2': {
            map: {
                fields: {
                    '20#1': {string_value: '1'},
                    '27#1': {string_value: '2'},
                }
            }
        },
        aa434968-c928-4908-9d99-75759809f44c#2: {
            map: {
                fields: {
                    '21#2': {string_value: '3'}
                }
            }
        },
    }
 * @param itemId Item ID or product ID
 * @param bagType Bag type, e.g. 1 for allocations, 2 for bulks
 * @param callback A function which you can supply, accepts
 * the dimensions and value of each allocation element
    Example:
    forEachBag(bag, '74d0cc6e-51da-4844-823f-03cfb36145dd', '2', (path, value) => {...})
    will execute the callback function with:
    ['20','1'], '1'
    ['27','1'], '2'
    ...
 */
export const forEachAllocationBag = (
    allocationBag: AllocationBag,
    itemId: string,
    bagType: AllocationBagType,
    callback: (dimensions: string[], value: string) => void,
) => {
    const bagValues = getBagValuesByRowId(bagType, allocationBag, itemId);

    bagValues.forEach((bagValue, breadcrumbKey) => {
        const dimensions = breadcrumbKey.split(BAG_VALUE_PARAMS_SEPARATOR);
        if (bagValue) {
            callback(dimensions, bagValue.get("string_value"));
        }
    });
};
export const getAllocationBagKind = (bagType: AllocationBagType | string) => AllocationBagType[bagType];
/**
 * Generate mutations necessary to get `sourceBagValues` to be same as 'destBagValues'
 * @param sourceBagValues
 * @param destBagValues
 * @param rowId
 * @param bagType
 * @returns
 */
export const generateReconciliationMutations = (
    sourceBagValues: BagValues,
    destBagValues: BagValues,
    rowId: string,
    bagType: AllocationBagType,
): IMutateAllocationBagValue[] => {
    const removeMutations: { [key: string]: IMutateAllocationBagValue } = {};
    const setMutations: { [key: string]: IMutateAllocationBagValue } = {};
    sourceBagValues.forEach((_bagValue, key) => {
        const destBagValue = destBagValues.get(key);
        const path = key.split(BAG_VALUE_PARAMS_SEPARATOR);

        // Override if there is entry in source bag.
        // If entry exists in source bag but not in dest bag, remove it.
        if (destBagValue) {
            const quantity = safeParseFloat(destBagValue?.get("string_value"), 0);
            setMutations[key] = {
                bagType,
                quantity,
                dimensionValues: path,
                rowId,
            };
        } else {
            removeMutations[key] = {
                bagType,
                quantity: 0,
                dimensionValues: path,
                rowId,
            };
        }
    });

    destBagValues.forEach((bagValue, key) => {
        // Only process key that hasn't been seen
        if (!removeMutations[key] && !setMutations[key]) {
            const path = key.split(BAG_VALUE_PARAMS_SEPARATOR);
            const quantity = safeParseFloat(bagValue?.get("string_value"), 0);
            setMutations[key] = {
                bagType,
                quantity,
                dimensionValues: path,
                rowId,
            };
        }
    });

    return Object.values(removeMutations).concat(Object.values(setMutations));
};

type IAllocationBagOperation =
    | {
          [OperationType.Set]: {
              item_id?: string;
              target_id?: string;
              value: {
                  allocation_dimension_value: string[];
                  quantity: string;
                  kind: typeof AllocationBagType;
              };
          };
      }
    | {
          [OperationType.Remove]: {
              item_id?: string;
              target_id?: string;
              value: {
                  allocation_dimension_value: string[];
                  quantity: string;
                  kind: typeof AllocationBagType;
              };
          };
      }
    | {
          [OperationType.Clear]: {
              item_id?: string;
              target_id?: string;
              value: {
                  kind: typeof AllocationBagType;
              };
          };
      };

const commandToAbbreviationMap = {
    [OperationType.Set]: "s",
    [OperationType.Remove]: "r",
    [OperationType.Clear]: "c",
};
/**
 * Generate payload to save to server
 * @param mutations
 */
export const createBatchMutations = (mutations: IAllocationBagOperation[], itemIdKey = "item_id") => {
    const batches = [];
    // Map to keep track of existing batch per row id
    const rowIdToIndexMap: { [key: string]: number } = {};
    // Map to keep track of existing operation per dimension values
    const keysToIndexMap: { [key: string]: number[] } = {};
    mutations.forEach((mutation) => {
        const command = Object.keys(mutation)[0];
        const commandAbbreviation = commandToAbbreviationMap[command];
        const rowId = mutation[command][itemIdKey];
        const dimensionValues = [...(mutation[command].value.allocation_dimension_value ?? [])];
        const lastDimensionValue = dimensionValues.pop();

        let batch, operation;
        if (rowIdToIndexMap[rowId] === undefined) {
            batch = {
                [itemIdKey]: rowId,
            };
            batches.push(batch);
            rowIdToIndexMap[rowId] = batches.length - 1;
        } else {
            batch = batches[rowIdToIndexMap[rowId]];
        }

        if (command === OperationType.Clear) {
            operation = mutation[command].value.kind;
            batch[commandAbbreviation] = (batch[commandAbbreviation] ?? []).concat([operation]);
        } else {
            const compositeId = [rowId, command].concat(dimensionValues).join("_");
            if (keysToIndexMap[compositeId] === undefined) {
                operation = {
                    p: dimensionValues,
                    a: {},
                };

                batch[commandAbbreviation] = (batch[commandAbbreviation] ?? []).concat([operation]);
                keysToIndexMap[compositeId] = [rowIdToIndexMap[rowId], batch[commandAbbreviation].length - 1];
            } else {
                const cachedRowId = keysToIndexMap[compositeId][0];
                const cachedOperationId = keysToIndexMap[compositeId][1];
                operation = batches[cachedRowId][commandAbbreviation][cachedOperationId];
            }

            operation.a[lastDimensionValue.toString()] = {
                ...(operation.a[lastDimensionValue.toString()] ?? { data: [] }),
            };
            operation.a[lastDimensionValue.toString()].data.push({
                k: mutation[command].value.kind,
                q: command === OperationType.Remove ? "" : mutation[command].value.quantity,
            });
        }
    });

    return batches;
};
export const getDeliveriesFromAllocationDimension = (dimensionValues: AllocationDimension[]) => {
    const validDeliveries = dimensionValues.reduce((acc, dimension) => {
        const { included, active } = dimension;
        if (active && included) {
            acc.push(dimension);
        }
        return acc;
    }, []);

    return validDeliveries;
};
